import React, { useContext, useEffect, useState } from "react";
import { Menu, Plus, X } from "lucide-react";
import { NavLink } from "react-router-dom";

import AuthContext from "../../contexts/AuthCtx";
import BuyStampsModal from "./BuyStampsModal";
import FeedbackWidget from "./FeedbackWidget";
import Sidebar from "./Sidebar";
import customStamp from "../../assets/icons/post-stamp.svg";

function DashboardLayout({
  children,
  onNewLetter,
  showFeedback = true,
  /** Pin children to the viewport height (dashboard board + swipe dots). */
  fillHeight = false,
}) {
  const AuthCtx = useContext(AuthContext);
  const [isBuyStampsModalOpen, setIsBuyStampsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const availableStamps = AuthCtx.totalStamps - AuthCtx.usedStamps;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isSidebarOpen]);

  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-white">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-greyLight/30 bg-dashboardSidebar px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-secondaryText transition hover:bg-white/70 lg:hidden"
            onClick={() => setIsSidebarOpen((open) => !open)}
            aria-label={isSidebarOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isSidebarOpen}
          >
            {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <NavLink to="/dashboard" className="truncate font-logo text-xl text-black sm:text-2xl">
            ByeByeRegrets
          </NavLink>
        </div>

        <div className="flex shrink-0 items-center gap-2 font-josefin text-base text-secondaryText sm:text-lg">
          <img width={28} height={28} src={customStamp} alt="" />
          <span className="font-medium">{availableStamps}</span>
          <button
            type="button"
            className="flex items-center justify-center text-brandPurple transition hover:text-primaryDark"
            onClick={() => setIsBuyStampsModalOpen(true)}
            aria-label="Acheter des timbres"
          >
            <Plus size={22} strokeWidth={2.25} />
          </button>
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1">
        {isSidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={closeSidebar}
            aria-label="Fermer le menu"
          />
        )}

        <Sidebar
          onNewLetter={onNewLetter}
          isMobileOpen={isSidebarOpen}
          onNavigate={closeSidebar}
          onCloseMobile={closeSidebar}
        />

        <main
          className={`min-h-0 min-w-0 flex-1 bg-white px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 ${
            fillHeight
              ? "flex flex-col overflow-hidden pb-1 sm:pb-3 lg:overflow-y-auto lg:pb-6"
              : "overflow-y-auto pb-4 sm:pb-6"
          }`}
        >
          {children}
        </main>
      </div>

      <BuyStampsModal
        open={isBuyStampsModalOpen}
        onClose={() => setIsBuyStampsModalOpen(false)}
        token={AuthCtx.userToken}
      />

      {showFeedback && <FeedbackWidget />}
    </div>
  );
}

export default DashboardLayout;
